import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import EconDropdown from '../../../../ui-kit/EconDropdown/EconDropdown'

import {FC, Suspense, useEffect, useRef, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Dropdown, Image, Modal, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {CSVLink} from 'react-csv'
import {useNavigate, useSearchParams} from 'react-router-dom'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {AVAILABLE_YEARS, FTA_IMPACT_SETTING_GUIDE} from '../../../libs/constant'
import {getISO2ByISO3, getNameByISO3} from '../../../libs/misc'

import {ImpactTable} from './components/ImpactTable'
import {SummaryCard} from './components/SummaryCard'
import {runSimulation} from './core/_requests'

import {SimulationResultRow} from './core/_models'
import {useFTAImpactContext} from '../../../contexts/fta-impact/FTAImpactContext'
import {useAuth} from '../../../modules/auth'

export type simulationParamType = {
  year: number
  rep: string
  par: string
  econ: string
  supplyElas: number
  subElas: number
  elasType: number
}

const ReactSwal = withReactContent(Swal)

const FTAImpactPage: FC = () => {
  const intl = useIntl()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const reporterISO3User = currentUser?.economy
  const {setDataTable, setYear, setPartner, setReporter} = useFTAImpactContext()
  const [showSettingSection, setShowSettingSection] = useState(true)
  const [showResultSection, setShowResultSection] = useState(false)
  const [simulationParam, setSimulationParam] = useState<simulationParamType>({
    year: AVAILABLE_YEARS[0],
    rep: '',
    par: '',
    econ: reporterISO3User || '',
    supplyElas: 999,
    subElas: 1.5,
    elasType: 1,
  })
  const [simulationTableData, setSimulationTableData] =
    useState<Array<SimulationResultRow> | null>()
  const [simulationSummaryData, setSimulationSummaryData] = useState<any | null>()
  const [repParams, setRepParams] = useSearchParams()
  const [modalParam, setModalParam] = useState({
    show: false,
    header: '',
    body: '',
  })
  const isSwitchedRef = useRef(false)
  let isButtonDisabled = !(simulationParam.rep && simulationParam.par)

  useEffect(() => {
    const rep = repParams.get('rep')
    const par = repParams.get('par')

    if (rep && par) {
      setSimulationParam((prev) => ({
        ...prev,
        rep,
        par,
      }))
    } else if (rep) {
      handleInputChange('rep', rep)
    } else if (par) {
      handleInputChange('par', par)
    }
  }, [])

  useEffect(() => {
    if (isSwitchedRef.current) {
      handleRunSimulation()
      isSwitchedRef.current = false // Reset the flag after calling handleRunSimulation
    }
  }, [simulationParam])

  const handleSelectRep = (econ: string) => {
    handleInputChange('rep', econ)
  }

  const handleSelectPar = (econ: string) => {
    handleInputChange('par', econ)
  }

  const handleInputChange = (field: keyof simulationParamType, value: any) => {
    setSimulationParam({...simulationParam, [field]: value})
  }

  const handleDisplayToggle = (param: any = undefined) => {
    if (param && !modalParam.show === true) {
      return setModalParam((prev) => {
        return {
          ...prev,
          show: !modalParam.show,
          header: param.header,
          body: param.body,
        }
      })
    }
    return setModalParam((prev) => {
      return {
        ...prev,
        show: !modalParam.show,
      }
    })
  }

  const simulateInputOutput = (dataTable) => {
    setDataTable(dataTable)
    setYear(simulationParam.year.toString())
    setReporter(simulationParam.rep)
    setPartner(simulationParam.par)
    navigate('/input-output-analysis')
  }

  const handleRunSimulation = () => {
    setShowResultSection(true)
    setSimulationTableData(null)
    setSimulationSummaryData(null)
    setShowSettingSection(false)
    runSimulation(axiosPrivate, simulationParam)
      .then((res) => {
        if (res.data.table_data.length > 0) {
          setSimulationTableData(res.data.table_data)
          setSimulationSummaryData(res.data.summary)
        } else {
          setShowResultSection(false)
          setShowSettingSection(true)
          ReactSwal.fire({
            title: 'Error! Data Not Found',
            html: `There is no commodity-related trade data available for <strong>${getNameByISO3(
              reporterISO3User
            )}</strong> between ${simulationParam.rep} and ${
              simulationParam.par
            }. Please check your parameters and try again.`,
            icon: 'error',
            showCloseButton: true,
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: '<i class="fa-solid fa-angles-left me-1 pb-1"></i>BACK',
            customClass: {
              title: 'fw-bold',
              cancelButton: 'btn btn-danger btn-sm',
            },
            padding: '1rem',
          })
          handleInputChange('rep', null)
          handleInputChange('par', null)
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }

  const handleBtnAdjustParams = () => {
    setShowSettingSection(true)
    setShowResultSection(false)
    // handleInputChange('rep',null)
    // handleInputChange('par',null)
  }

  const handleSwitchCountries = () => {
    setShowSettingSection(true)
    setShowResultSection(false)
    setSimulationParam((prev) => {
      isSwitchedRef.current = true
      return {
        ...prev,
        rep: prev.par,
        par: prev.rep,
      }
    })
  }

  const generateCSVData = () => {
    if (!simulationTableData || simulationTableData.length === 0) {
      return []
    }

    return simulationTableData.map((row) => {
      const econData = row.affected_econ?.find((data) => data.econ === reporterISO3User)
      const impact = econData ? econData.tc + econData.td : 'N/A'
      return {
        hs_code: row.hs_code,
        description: row.desc,
        current_trade_value: row.trade_val,
        current_tariff: row.tariff_old,
        simulated_tariff: row.tariff_new,
        change_in_trade_value: row.tc + row.td,
        change_in_trade_value_percentage: (((row.tc + row.td) / row.trade_val) * 100).toFixed(2),
        impact_for_econ: impact,
        other_affected_econ: JSON.stringify(row.affected_econ),
      }
    })
  }

  const handleBtnExportCSV = () => {
    if (!simulationTableData || simulationTableData.length === 0) {
      return false
    }
  }

  const tinaLinked =
    simulationParam.rep && simulationParam.par
      ? `${process.env.REACT_APP_TINA_BASE_URL}/app/dashboard/${simulationParam.rep}-${simulationParam.par}/offensive-list`
      : `${process.env.REACT_APP_TINA_BASE_URL}/app/start/${simulationParam.rep}/home`
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: 'MENU.FTA_IMPACT'})}
      </PageTitle>
      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex justify-content-start align-items-center'>
              <Image src='media/tina/img-result-documents.jpg' thumbnail width={110} />
              <div className='ms-5 d-flex flex-column justify-content-start'>
                <h3 className='text-primary'>
                  RTA Impact Simulation
                  <span className='text-gray-700'>
                    <span className='fa-solid fa-angles-right px-1'></span>
                    impact on {getNameByISO3(reporterISO3User)}
                  </span>
                </h3>
                <div className='text-gray-700'>
                  Evaluate the impact on {getNameByISO3(reporterISO3User)} of bilateral free trade
                  agreements between countries other than {getNameByISO3(reporterISO3User)} (full
                  tariff liberalization). Select reporter country (liberalization tariffs) and
                  partner country (exporter). You can switch them after running the simulation. You
                  may wish to build a negotiation list to narrow down the list of products{' '}
                  <a target='_blank' href={tinaLinked} rel='noreferrer'>
                    here
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {showSettingSection ? (
        <Row className='mb-5'>
          <Col md={6}>
            <Card
              className='card h-100 bgi-no-repeat bgi-size-cover'
              style={{
                backgroundImage: `url('media/tina/img-bg-tina-head.jpg')`,
              }}
            >
              <Card.Body
                className='p-15 text-white d-flex flex-column justify-content-center'
                style={{
                  maxWidth: '80%',
                }}
              >
                <h2 className='text-white fw-normal mb-10'>
                  What would be the impact on {getNameByISO3(reporterISO3User)} if other countries
                  liberalize tariffs?
                </h2>
                <p className='mb-10'>
                  TINA can run a partial-equilibrium trade policy simulation using a model. TINA
                  uses the following data to calculate trade creation and trade diversion:
                </p>
                <ul
                  style={{
                    paddingInlineStart: '0px',
                    listStyle: 'none',
                  }}
                >
                  <li>
                    <i className='la la-check me-5'></i>
                    <span>Bilateral trade flows </span>
                  </li>
                  <li>
                    <i className='la la-check me-5'></i>
                    <span>Import demand elasticities </span>
                  </li>
                  <li>
                    <i className='la la-check me-5'></i>
                    <span>Bound, MFN &amp; preferential tariffs </span>
                  </li>
                  <li>
                    <i className='la la-check me-5'></i>
                    <strong>Simulation parameters set by you</strong>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title>
                  <span className='fa fa-gear px-2'></span>
                  <h3 className='text-gray-700'>Settings for the simulation</h3>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label column className='text-end text-primary'>
                      Import data year :
                    </Form.Label>
                    <Col md={6} className='d-flex align-items-center'>
                      <i
                        className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                        onClick={() =>
                          handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.TRADE_DATA_YEAR)
                        }
                      ></i>
                      <div className='flex-grow-1'>
                        <Dropdown className='pt-2'>
                          <Dropdown.Toggle
                            variant='label-dropdown'
                            id=''
                            className='btn btn-sm btn-bold dropdown-toggle fw-normal dss_graph_trade_partners_available_years_latest w-100 text-start'
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            {simulationParam.year}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {AVAILABLE_YEARS.map((year) => {
                              return (
                                <Dropdown.Item
                                  key={year}
                                  onClick={() => handleInputChange('year', year)}
                                >
                                  {year}
                                </Dropdown.Item>
                              )
                            })}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label column md={6} className='text-end text-primary'>
                      Reporter country :
                    </Form.Label>

                    <Col md={6} className='d-flex align-items-center'>
                      <i
                        className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                        onClick={() => handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.REPORTER)}
                      ></i>
                      <div className='flex-grow-1'>
                        <EconDropdown
                          onSelect={handleSelectRep}
                          disabled={repParams.get('rep') === reporterISO3User ? true : false}
                          econ={repParams.get('rep') || undefined}
                        />
                      </div>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label className='text-end text-primary' column>
                      Partner country :
                    </Form.Label>
                    <Col md={6} className='d-flex align-items-center'>
                      <i
                        className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                        onClick={() => handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.PARTNER)}
                      ></i>
                      <div className='flex-grow-1'>
                        <EconDropdown
                          onSelect={handleSelectPar}
                          econ={repParams.get('par') || undefined}
                        />
                      </div>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label className='text-end text-primary' column>
                      Supply elasticity :
                    </Form.Label>
                    <Col md={6} className='d-flex align-items-center'>
                      <i
                        className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                        onClick={() =>
                          handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.SUPPLY_ELASTICITY)
                        }
                      ></i>
                      <Form.Control
                        type='number'
                        className='form-control-sm mt-1'
                        value={simulationParam.supplyElas}
                        onChange={(e) => handleInputChange('supplyElas', +e.target.value)}
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label className='text-end text-primary' column>
                      Substitution elasticity :
                    </Form.Label>
                    <Col md={6} className='d-flex align-items-center'>
                      <i
                        className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                        onClick={() =>
                          handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.SUBSTITUTION_ELASTICITY)
                        }
                      ></i>
                      <Form.Control
                        type='number'
                        className='form-control-sm mt-1'
                        value={simulationParam.subElas}
                        onChange={(e) => handleInputChange('subElas', +e.target.value)}
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className='mb-6' controlId=''>
                    <Form.Label className='text-end text-primary' column>
                      Import demand elasticity :
                    </Form.Label>
                    <Col md={6}>
                      <Row>
                        <Col className='d-flex align-items-center'>
                          <i
                            className='fa-solid fa-circle-question text-primary cursor-pointer me-3'
                            onClick={() =>
                              handleDisplayToggle(FTA_IMPACT_SETTING_GUIDE.IMPORT_DEMAND_ELASTICITY)
                            }
                          ></i>
                          <Button
                            variant=''
                            className={`btn btn-radio ${
                              simulationParam.elasType === 1
                                ? 'btn-primary text-white'
                                : 'btn-outline btn-outline-primary'
                            } 
                              w-100 btn-sm fw-bold`}
                            onClick={() => handleInputChange('elasType', 1)}
                          >
                            <span
                              className={`fa-solid fa-${
                                simulationParam.elasType === 1 ? 'check' : 'xmark'
                              } px-1`}
                            ></span>
                            Utoktham, Kravchenko and Duval (2022)
                          </Button>
                        </Col>
                      </Row>
                      <Row className='mt-2'>
                        <Col className='d-flex align-items-center'>
                          <Button
                            variant=''
                            className={`ms-7 btn btn-radio ${
                              simulationParam.elasType === 2
                                ? 'btn-primary text-white'
                                : 'btn-outline btn-outline-primary'
                            } w-100 btn-sm fw-bold`}
                            onClick={() => handleInputChange('elasType', 2)}
                          >
                            <span
                              className={`fa-solid fa-${
                                simulationParam.elasType === 2 ? 'check' : 'xmark'
                              } px-1`}
                            ></span>
                            Kee, Nicita and Olarreage (2008)
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Form.Group>
                  <Row>
                    <Col className='d-flex justify-content-end'>
                      <Button
                        className='w-100 btn btn-warning text-black fw-bold'
                        disabled={isButtonDisabled}
                        onClick={handleRunSimulation}
                      >
                        Submit & Simulate
                        <span className='fa-solid fa-angles-right px-1'></span>
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        ''
      )}
      {showResultSection ? (
        <>
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100 d-flex justify-content-between align-items-center'>
                    <div>
                      <h3>
                        <i className='fa-regular fa-star me-5'></i>
                        Summary of simulation results
                      </h3>
                      <small>
                        {getNameByISO3(simulationParam.par)} removes tariffs on all imports from{' '}
                        {getNameByISO3(simulationParam.rep)}
                      </small>
                    </div>
                    <div>
                      <Button
                        className='btn-warning text-black fw-bold me-2'
                        size='sm'
                        onClick={handleSwitchCountries}
                        title='Switch Reporter and Partner Country'
                        disabled={!(simulationTableData && simulationTableData.length > 0)}
                      >
                        <span
                          className={`fi fi-${(
                            getISO2ByISO3(simulationParam.rep) || ''
                          ).toLowerCase()} me-2`}
                        ></span>
                        <i className='fa-solid fa-repeat p-0 fa-lg text-black'></i>
                        <span
                          className={`fi fi-${(
                            getISO2ByISO3(simulationParam.par) || ''
                          ).toLowerCase()} ms-2`}
                        ></span>
                      </Button>
                      <Button
                        className='btn-warning text-black fw-bold'
                        size='sm'
                        onClick={handleBtnAdjustParams}
                      >
                        <i className='text-black bi bi-sliders me-3'></i>
                        Adjust Parameters
                      </Button>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <SummaryCard
                    summary={simulationSummaryData}
                    rep={getNameByISO3(simulationParam.rep)}
                    par={getNameByISO3(simulationParam.par)}
                    year={simulationParam.year}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className='table-responsive table-fta-impact'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100 d-flex justify-content-between align-items-center'>
                    <div>
                      <h3>
                        <i className='fa-solid fa-barcode me-5'></i>
                        Detailed Breakdown
                      </h3>
                    </div>
                    <CSVLink
                      data={generateCSVData() || []}
                      filename={`fta-impact-simulation-result-${simulationParam.rep}_${simulationParam.par}`}
                      onClick={handleBtnExportCSV}
                    >
                      <Button variant='outline' className='text-gray-700' size='sm'>
                        <i className='bi bi-download me-3'></i>
                        Export to CSV
                      </Button>
                    </CSVLink>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <ImpactTable
                    rows={simulationTableData}
                    params={{
                      rep: simulationParam.rep,
                      par: simulationParam.par,
                      year: simulationParam.year,
                    }}
                    simulateInputOutput={simulateInputOutput}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        ''
      )}
      {/* {
        showNoDataAlertSection
        ?(
          <>
            <Row className="mb-5">
              <div className="alert alert-danger d-flex align-items-center p-5 mb-10">
                <span className="svg-icon svg-icon-2hx svg-icon-danger me-3">...</span>
                
                <div className="d-flex flex-column">
                  <h5 className="mb-1">Data Not Found</h5>
                  <span>There is no commodity-related trade data available for ${getNameByISO3(reporterISO3User)}. Please check your parameters and try again.</span>
                </div>
              </div>
            </Row>
          </>
        ):''
      } */}
      <Modal show={modalParam.show} onHide={handleDisplayToggle} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className='fa fa-exclamation-circle me-3'></i>
            {modalParam.header}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p dangerouslySetInnerHTML={{__html: modalParam.body}}></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline' onClick={handleDisplayToggle}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default FTAImpactPage
